import { Tags } from '../enum/tags';
import { apiSlice } from './http';

// Enums
export enum DivergenceTypeEnum {
  SEQUENCE_GAP = 'SEQUENCE_GAP',
  SEQUENCE_GAP_GFT = 'SEQUENCE_GAP_GFT',
  EXCLUSIVE_DOC001 = 'EXCLUSIVE_DOC001',
  EXCLUSIVE_VALIDATOR = 'EXCLUSIVE_VALIDATOR',
  EXCLUSIVE_GFT_LIVRO = 'EXCLUSIVE_GFT_LIVRO',
  EXCLUSIVE_DOC001_GFT = 'EXCLUSIVE_DOC001_GFT',
  STATUS_DIVERGENT = 'STATUS_DIVERGENT',
  STATUS_DIVERGENT_GFT = 'STATUS_DIVERGENT_GFT',
  STATUS_DIVERGENT_GFT_VALIDATOR = 'STATUS_DIVERGENT_GFT_VALIDATOR',
}

export const DivergenceSeverity = {
  CRITICAL: 'CRITICAL',
  TEMPORAL: 'TEMPORAL',
  INVESTIGATION: 'INVESTIGATION',
} as const;

export type DivergenceSeverityType = typeof DivergenceSeverity[keyof typeof DivergenceSeverity];

// Filter DTO
export interface ReconciliationFilterDto {
  startDate: string;
  endDate: string;
  includeGaps?: boolean;
  includeStatus?: boolean;
  includeGftLivro?: boolean;
  types?: DivergenceTypeEnum[];
  voidedLogix?: boolean;
  doc001Status?: string;
  validatorStatus?: string;
  gftStatus?: string;
  store?: string;
  stores?: string[];
  cashier?: string;
  cashiers?: string[];
  severity?: DivergenceSeverityType;
  search?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
}

// Unified Divergence DTO
export interface UnifiedDivergenceDto {
  id: string;
  type: DivergenceTypeEnum;
  store: string;
  cashier: string;
  receipt: number;
  date: string;
  detectedAt: string;
  previousReceipt?: number;
  nextReceipt?: number;
  gapSize?: number;
  existsInValidator?: boolean;
  validatorGapStatus?: string | null;
  simplifiedStatus?: string;
  doc001Status?: string;
  validatorStatus?: string;
  situation?: string;
  status?: string;
  reason?: string;
  calculatedDoc001Status?: string;
  situation2Raw?: string;
  discrepancyType?: string;
  details?: string;
  severity?: DivergenceSeverityType;
  gftLivroStatus?: string;
  existsInGftLivro?: boolean;
  divergenceOrigin?: string;
  voidedLogix?: boolean;
  storeCnpj?: string;
}

// Summary DTO
export interface ReconciliationSummaryDto {
  totalDoc001: number;
  totalValidator: number;
  totalGftLivro?: number;
  totalDivergences: number;
  reconciliationStatus: 'SUCCESS' | 'PARTIAL' | 'ERROR';
  statistics: {
    sequenceGaps: number;
    exclusiveDoc001: number;
    exclusiveValidator: number;
    statusDivergent: number;
    criticalDivergences: number;
    temporalDivergences: number;
    investigationDivergences: number;
    sequenceGapsGft?: number;
    exclusiveGftLivro?: number;
    statusDivergentGft?: number;
  };
}

export interface PaginationDto {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface DivergencesByTypeDto {
  divergences: UnifiedDivergenceDto[];
  summaryByType: Record<DivergenceTypeEnum, { total: number }>;
}

export interface ReconciliationResponseDto {
  period: { startDate: string; endDate: string };
  summary: ReconciliationSummaryDto;
  divergences: DivergencesByTypeDto;
  pagination: PaginationDto;
  executedAt?: string;
  processingTime?: number;
  metadata?: {
    gftIncluded: boolean;
    gftError?: { code: string; message: string };
  };
}

export interface JobSubmittedResponseDto {
  jobId: string;
  statusUrl: string;
}

export const AsyncJobStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;

export type AsyncJobStatusType = typeof AsyncJobStatus[keyof typeof AsyncJobStatus];

export interface JobStageDto {
  name: string;
  status: 'ok' | 'failed' | 'skipped';
  rows?: number;
  error?: string;
  timeMs?: number;
}

export interface JobStatusDto {
  jobId: string;
  status: AsyncJobStatusType;
  progress: number;
  message: string;
  paginatedResult?: ReconciliationResponseDto;
  downloadUrl?: string;
  error?: string;
  stages?: JobStageDto[];
  createdAt: string;
  updatedAt: string;
}

export const taxReconciliationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    executeReconciliation: builder.query<JobSubmittedResponseDto, ReconciliationFilterDto>({
      query: (params) => ({
        url: '/tax-reconciliation',
        method: 'GET',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          includeGaps: params.includeGaps,
          includeStatus: params.includeStatus,
          includeGftLivro: params.includeGftLivro,
          types: params.types,
          voidedLogix: params.voidedLogix,
          doc001Status: params.doc001Status,
          validatorStatus: params.validatorStatus,
          gftStatus: params.gftStatus,
          store: params.store,
          stores: params.stores,
          severity: params.severity,
          search: params.search,
          sortOrder: params.sortOrder,
          page: params.page,
          limit: params.limit,
        },
      }),
      providesTags: [Tags.TAX_RECONCILIATION],
    }),

    getReconciliationJobStatus: builder.query<JobStatusDto, string>({
      query: (jobId) => ({
        url: `/tax-reconciliation/status/${jobId}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, jobId) => [
        { type: Tags.TAX_RECONCILIATION_JOB, id: jobId },
      ],
    }),

    getActiveJob: builder.query<JobStatusDto, void>({
      query: () => '/tax-reconciliation/active',
      providesTags: [Tags.TAX_RECONCILIATION_JOB],
    }),

    getJobResults: builder.query<JobStatusDto, { jobId: string; page?: number; limit?: number }>({
      query: ({ jobId, ...params }) => ({
        url: `/tax-reconciliation/status/${jobId}`,
        params,
      }),
      providesTags: (_result, _error, { jobId }) => [
        { type: Tags.TAX_RECONCILIATION_JOB, id: jobId },
      ],
    }),

    cancelJob: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `/tax-reconciliation/status/${jobId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [Tags.TAX_RECONCILIATION_JOB],
    }),

    clearReconciliationCache: builder.mutation<void, { startDate?: string; endDate?: string }>({
      query: (params) => ({
        url: '/tax-reconciliation/cache',
        method: 'DELETE',
        params,
      }),
      invalidatesTags: [Tags.TAX_RECONCILIATION],
    }),

    purge: builder.mutation<{ message: string; cleaned: string[] }, void>({
      query: () => ({
        url: '/tax-reconciliation/purge',
        method: 'DELETE',
      }),
      invalidatesTags: [Tags.TAX_RECONCILIATION, Tags.TAX_RECONCILIATION_JOB],
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyExecuteReconciliationQuery,
  useGetReconciliationJobStatusQuery,
  useLazyGetActiveJobQuery,
  useLazyGetJobResultsQuery,
  useCancelJobMutation,
  useClearReconciliationCacheMutation,
  usePurgeMutation,
} = taxReconciliationApi;
